import React, { useEffect, useState } from "react";
import { api, authHeaders } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Loader2, Ticket, CheckCircle2, PiggyBank, Wallet, Store, Clock } from "lucide-react";
import { euro } from "@/lib/format";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="rounded-xl bg-slate-50 p-3">
    <div className="flex items-center gap-1.5 text-slate-400 text-xs font-semibold">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</div>
    <p className={`text-xl font-extrabold mt-0.5 ${accent || "text-slate-900"}`}>{value}</p>
  </div>
);

const dm = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`; };
const dmt = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

export default function VendorOfferDetail({ offer, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/vendor/offers/${offer.offer_id}/claims`, { headers: authHeaders("vendor") })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [offer.offer_id]);

  const chartData = (data?.series || []).map((s) => ({ giorno: dm(s.date), Richieste: s.requests, Riscattati: s.redemptions }));
  const redeemedList = (data?.claims || []).filter((c) => c.redeemed);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="vendor-offer-detail">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold truncate">{offer.title}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#E63946]" /></div>
        ) : !data ? (
          <p className="py-10 text-center text-slate-400">Impossibile caricare i dati.</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Richieste" value={data.count} icon={Ticket} />
              <Stat label="Riscattati" value={data.redeemed_count} icon={CheckCircle2} accent="text-emerald-600" />
              <Stat label="Incasso" value={euro(data.revenue)} icon={Wallet} />
              <Stat label="Risparmio dato" value={euro(data.saved)} icon={PiggyBank} />
            </div>

            <div>
              <p className="text-sm font-bold text-slate-700 mb-1">Andamento (ultimi 14 giorni)</p>
              <div className="rounded-2xl border border-slate-100 p-2" data-testid="offer-chart">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} margin={{ left: -22, right: 8, top: 6 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="giorno" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={1} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Richieste" fill="#fca5a5" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Riscattati" fill="#E63946" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div>
              <p className="text-sm font-bold text-slate-700 mb-2">Riscatti ({redeemedList.length})</p>
              {redeemedList.length === 0 ? (
                <div className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-400">Nessun coupon ancora riscattato.</div>
              ) : (
                <div className="space-y-2" data-testid="offer-redemptions">
                  {redeemedList.map((c) => (
                    <div key={c.claim_id} className="flex items-center gap-3 rounded-xl border border-slate-100 p-2.5">
                      <div className="h-9 w-9 rounded-lg bg-[#FFE4E6] flex items-center justify-center overflow-hidden shrink-0">
                        {c.client_photo ? <img src={c.client_photo} alt="" className="h-full w-full object-cover" /> : <Store className="h-4 w-4 text-[#E63946]" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-800 text-sm truncate">{c.client_name}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {dmt(c.redeemed_at)}</p>
                      </div>
                      <div className="text-right shrink-0">
                        {c.amount_spent != null ? (
                          <>
                            <p className="font-extrabold text-slate-900 text-sm">{euro(c.amount_spent)}</p>
                            {c.discount_eur != null && <p className="text-[11px] text-emerald-600 font-semibold">-{euro(c.discount_eur)}</p>}
                          </>
                        ) : (
                          <p className="text-xs text-slate-400">Importo n.d.</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
